import {importLibrary} from "@googlemaps/js-api-loader";
import React, {useEffect, useRef, useState} from "react";
import {useIntl} from "react-intl";
import {createCarMarker} from "../createCarMarker";
import {useDarkMode} from "../hooks/useDarkMode";
import {Location} from "../model/rideInfo";
import {getTheme} from "./getTheme";

/* eslint-disable react-hooks/exhaustive-deps */

export function Map({
    pickup,
    destination,
    currentStop,
    deliveryStops,
    driver,
    route,
    destinationPinIcon,
    carColor,
    inQueue = false,
    wrapperClassName,
}: {
    pickup?: Location;
    destination?: Location;
    currentStop?: Location;
    deliveryStops?: Location[];
    driver?: Location;
    route?: string;
    destinationPinIcon?: string;
    carColor?: string;
    inQueue?: boolean;
    wrapperClassName: string;
}) {
    const mapElementRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<google.maps.Map>();
    const pickupRef = useRef<google.maps.marker.AdvancedMarkerElement>();
    const destinationRef = useRef<google.maps.marker.AdvancedMarkerElement>();
    const driverRef = useRef<google.maps.OverlayView>();
    const routeRef = useRef<google.maps.Polyline>();
    const deliveryStopsRef = useRef<google.maps.marker.AdvancedMarkerElement[]>();
    const [mapInitialized, setMapInitialized] = useState(false);
    const [boundsInitialized, setBoundsInitialized] = useState(false);
    const [lastInteractionTimestamp, setLastInteractionTimestamp] = useState(0);
    const zoomListenerEnabled = useRef(false);
    const dragStartListenerEnabled = useRef(false);
    const isDark = useDarkMode();
    const routeColor = isDark ? "#e8eaed" : "#002250";
    const backgroundColor = isDark ? "#242f3e" : "#e8eaed";
    const intl = useIntl();

    const getCarIcon = (carColor: string | undefined) => {
        switch (carColor) {
            case "BLACK":
                return "/car-pins/Black.png";
            case "BLUE":
                return "/car-pins/Blue.png";
            case "BROWN":
                return "/car-pins/Brown.png";
            case "GREEN":
                return "/car-pins/Green.png";
            case "ORANGE":
                return "/car-pins/Orange.png";
            case "PURPLE":
                return "/car-pins/Purple.png";
            case "RED":
                return "/car-pins/Red.png";
            case "SILVER":
                return "/car-pins/Silver.png";
            case "YELLOW":
                return "/car-pins/Yellow.png";
            case "WHITE":
                return "/car-pins/White.png";
            default:
                return "/car-pins/White.png";
        }
    };

    // init map
    useEffect(() => {
        let cancelled = false;

        (async () => {
            const {Map: GoogleMap} = (await importLibrary("maps")) as google.maps.MapsLibrary;
            const {AdvancedMarkerElement} = (await google.maps.importLibrary(
                "marker"
            )) as google.maps.MarkerLibrary;

            if (cancelled) return;

            const map = new GoogleMap(mapElementRef.current!, {
                center: {lat: 50.0, lng: 14.0},
                zoom: 8,
                maxZoom: 18,
                clickableIcons: false,
                zoomControl: false,
                disableDefaultUI: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                gestureHandling: "greedy",
                backgroundColor: backgroundColor,
                colorScheme: google.maps.ColorScheme.FOLLOW_SYSTEM,
                mapId: appConfig.googleMapsMapId,
            });

            const carMarker = createCarMarker({
                map: map,
                size: new google.maps.Size(52, 52),
                icon: getCarIcon(carColor),
                rotation: driver?.bearing ?? 0,
                latlng: new google.maps.LatLng(0, 0),
            });

            map.addListener("dragstart", function () {
                if (dragStartListenerEnabled.current !== true) {
                    return;
                }
                setLastInteractionTimestamp(Date.now());
            });

            map.addListener("idle", function () {
                carMarker.setMap(map);
                dragStartListenerEnabled.current = true;
                zoomListenerEnabled.current = true;
            });

            map.addListener("zoom_changed", function () {
                carMarker.setMap(map);
                if (zoomListenerEnabled.current !== true) {
                    return;
                }
                setLastInteractionTimestamp(Date.now());
            });

            mapRef.current = map;
            driverRef.current = carMarker;
            pickupRef.current = new AdvancedMarkerElement({
                content: createPinImage(`/${getTheme()}/pin-0.svg`),
                zIndex: 1,
            });
            destinationRef.current = new AdvancedMarkerElement({
                content: createPinImage(`/${getTheme()}/pin-1.svg`),
                zIndex: 2,
            });
            routeRef.current = new google.maps.Polyline({
                strokeColor: routeColor,
                strokeOpacity: 0.9,
                strokeWeight: 3,
            });

            setMapInitialized(true);
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    function convertPosition(position: Location): google.maps.LatLngLiteral {
        return {lat: position.lat, lng: position.lon};
    }

    function createPinImage(src: string): HTMLImageElement {
        const img = document.createElement("img");
        img.src = src;
        return img;
    }

    function setMarkerPosition(
        position: Location | null | undefined,
        marker?: google.maps.marker.AdvancedMarkerElement
    ) {
        if (position == null) {
            if (marker) marker.map = null;
        } else {
            if (marker) {
                marker.position = convertPosition(position);
                marker.map = mapRef.current ?? null;
            }
        }
    }

    function setCarPosition(position: Location | null | undefined, marker?: any) {
        if (position == null) {
            marker?.setMap(null);
        } else {
            marker?.setPosition(new google.maps.LatLng(position.lat, position.lon));
            if (position.bearing != null) {
                marker?.setRotation(position.bearing);
            }
        }
    }

    // update markers
    useEffect(() => {
        if (!mapInitialized) return;

        setMarkerPosition(pickup, pickupRef.current);
        setMarkerPosition(destination, destinationRef.current);
        setCarPosition(driver, driverRef.current);

        // route
        if (route == null) {
            routeRef.current?.setMap(null);
        } else {
            routeRef.current?.setPath(google.maps.geometry.encoding.decodePath(route));
            routeRef.current?.setMap(mapRef.current ?? null);
        }

        if (deliveryStops != null) {
            deliveryStopsRef.current = deliveryStops.map((stop, i) => {
                // try to recycle marker
                const oldMarker = (deliveryStopsRef.current ?? [])[i];
                const marker =
                    oldMarker ??
                    new google.maps.marker.AdvancedMarkerElement({
                        map: mapRef.current,
                        content: createPinImage("/light/point.svg"),
                        zIndex: 0,
                    });

                marker.position = convertPosition(stop);

                return marker;
            });
        }
    });

    // destination icon
    useEffect(() => {
        if (destinationRef.current?.content) {
            (destinationRef.current.content as HTMLImageElement).src =
                destinationPinIcon ?? `/${getTheme()}/pin-1.svg`;
        }
    }, [destinationRef.current, destinationPinIcon]);

    // update bounds
    useEffect(() => {
        if (!mapInitialized) return;

        const isBlockedByLastInteraction = Date.now() - lastInteractionTimestamp < 30000;
        if (isBlockedByLastInteraction) {
            return;
        }

        const map = mapRef.current;
        const mapBounds = map?.getBounds();

        let positions: google.maps.LatLngLiteral[];

        if (currentStop == null) {
            positions = [pickup, destination, driver, currentStop]
                .concat(deliveryStops)
                .filter((position) => position != null)
                .map((position) => convertPosition(position!));
        } else {
            positions = [driver, currentStop]
                .filter((position) => position != null)
                .map((position) => convertPosition(position!));
        }

        const shouldFitBounds =
            !boundsInitialized ||
            positions.some((position) => mapBounds?.contains(position) === false);
        if (!shouldFitBounds) {
            return;
        }

        const bounds = new google.maps.LatLngBounds();
        positions.forEach((position: google.maps.LatLngLiteral) => {
            bounds.extend(position);
        });
        if (bounds.isEmpty()) {
            return;
        }

        dragStartListenerEnabled.current = false;
        zoomListenerEnabled.current = false;

        map?.fitBounds(bounds);
        setBoundsInitialized(true);
    }, [
        mapInitialized,
        boundsInitialized,
        pickup?.lat,
        pickup?.lon,
        destination?.lat,
        destination?.lon,
        driver?.lat,
        driver?.lon,
        currentStop?.lat,
        currentStop?.lon,
    ]);

    //set theme
    useEffect(() => {
        if (!mapInitialized) return;

        mapRef.current?.setOptions({backgroundColor: backgroundColor});
        routeRef.current?.setOptions({strokeColor: routeColor});

        const theme = getTheme();
        if (pickupRef.current?.content) {
            (pickupRef.current.content as HTMLImageElement).src = `/${theme}/pin-0.svg`;
        }
        if (destinationRef.current?.content) {
            (destinationRef.current.content as HTMLImageElement).src =
                destinationPinIcon ?? `/${theme}/pin-1.svg`;
        }
    }, [isDark, mapInitialized]);

    useEffect(() => {
        const overlayDiv = document.createElement("div");
        overlayDiv.textContent = intl.formatMessage({id: "courier_is_finishing_previous_job"});
        overlayDiv.className = "overlay";

        if (mapElementRef.current && inQueue) {
            mapElementRef.current.appendChild(overlayDiv);
        }

        return () => {
            if (mapElementRef.current && overlayDiv.parentNode === mapElementRef.current) {
                mapElementRef.current.removeChild(overlayDiv);
            }
        };
    }, [inQueue]);

    return (
        <div
            className={wrapperClassName}
            ref={mapElementRef}
        />
    );
}
